/**
 * Serviço de API — Brandão Barbearia
 *
 * Mecanismo de autenticação (duplo, para compatibilidade mobile):
 *   1. Cookie HttpOnly ('admin_token' / 'client_token') — preferido, imune a XSS.
 *   2. Header Authorization: Bearer <token> — fallback para navegadores mobile
 *      que bloqueiam cookies de terceiros (iOS Safari ITP, WebViews in-app).
 *
 *   O token JWT é salvo no localStorage apenas como fallback de autenticação.
 *   credentials: 'include' continua ativo para que o cookie seja enviado quando disponível.
 *   O backend aceita ambos (cookie tem prioridade sobre header).
 */

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// ============== HELPERS ==============

/**
 * Retorna o token JWT armazenado no localStorage (admin ou client).
 * Usado como fallback quando o cookie HttpOnly não é enviado pelo browser.
 */
const getStoredToken = () =>
  localStorage.getItem('adminToken') || localStorage.getItem('clientToken');

/**
 * Realiza uma request JSON.
 * auth=true inclui 'credentials: include' para enviar o cookie HttpOnly
 * e adiciona o header Authorization: Bearer como fallback para mobile.
 */
const request = async (method, path, body = null, auth = false) => {
  const headers = { 'Content-Type': 'application/json' };

  // Fallback: envia o JWT via header para browsers que bloqueiam cookies cross-domain
  if (auth) {
    const token = getStoredToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const options = {
    method,
    headers,
    // 'include' sempre — o cookie é enviado apenas quando o servidor
    // o tiver definido (login). Em rotas públicas não há cookie, sem impacto.
    credentials: 'include',
  };

  if (body) {
    options.body = JSON.stringify(body);
  }

  const response = await fetch(`${BASE_URL}${path}`, options);

  if (!response.ok) {
    let error = { detail: `Erro ${response.status}` };
    try {
      const text = await response.text();
      if (text) error = JSON.parse(text);
    } catch (_) {}

    const detail = error.detail;
    const message = Array.isArray(detail)
      ? detail.map(e => e.msg || JSON.stringify(e)).join(' | ')
      : (typeof detail === 'string' ? detail : `Erro ${response.status}`);

    const err = new Error(message);
    err.response = { data: error, status: response.status };
    throw err;
  }

  const text = await response.text();
  const data = text ? JSON.parse(text) : null;
  return { data };
};


// ============== PUBLIC API ==============

export const getUnits = () => request('GET', '/units/');

export const getUnit = (id) => request('GET', `/units/${id}/`);

export const getBarbers = (unitId) => {
  const qs = unitId ? `?unit_id=${unitId}` : '';
  return request('GET', `/barbers/${qs}`);
};

/** Busca barbeiros para o painel admin — inclui barbeiros inativos. */
export const getBarberAdmin = (unitId) => {
  const params = new URLSearchParams({ include_inactive: 'true' });
  if (unitId) params.append('unit_id', unitId);
  return request('GET', `/barbers/?${params.toString()}`, null, true);
};


export const getBarber = (id) => request('GET', `/barbers/${id}`);

export const getServices = () => request('GET', '/services/');

export const getService = (id) => request('GET', `/services/${id}`);

export const getAvailableSlots = (barberId, date, serviceId) =>
  request('GET', `/appointments/available-slots?barber_id=${barberId}&date=${date}&service_id=${serviceId}`);

/**
 * Cria um agendamento.
 *
 * @param {Object} data
 * @param {string} data.appointment_date  — ISO 8601: "2025-03-15T10:30:00"
 * @param {string} data.barber_id
 * @param {string} data.service_id
 * @param {string} data.unit_id
 * @param {string} data.client_name
 * @param {string} data.client_phone
 * @param {string} [data.client_email]
 * @param {string} data.payment_type     — "20%" | "100%"
 * @param {boolean} [data.use_subscription]
 * @param {string} [data.notes]
 */
export const createAppointment = (data) => request('POST', '/appointments/', data);

/** Busca agendamento por ID (requer autenticação de admin/employee). */
export const getAppointment = (id) => request('GET', `/appointments/${id}`, null, true);

/** Cancela agendamento pelo próprio cliente via verificação de telefone (sem login admin). */
export const cancelAppointmentPublic = (id, phone, is_reschedule = false) =>
  request('PUT', `/appointments/${id}/cancel-public`, { phone, is_reschedule });

/** Configura recorrência para um agendamento existente (sem login admin). */
export const setAppointmentRecurrence = (id, recurrence_type, client_phone) =>
  request('POST', `/appointments/${id}/recurrence`, { recurrence_type, client_phone });

/** Busca agendamento por ID SEM autenticação — para a ConfirmationPage do cliente. */
export const getAppointmentPublic = (id) => request('GET', `/appointments/${id}/public`);

export const getAppointmentsByPhone = (phone) => {
  const digits = phone.replace(/\D/g, '');
  return request('GET', `/clients/buscar?phone=${digits}`).then(async (clientRes) => {
    const client = clientRes.data;
    const appointmentsRes = await request('GET', `/clients/${client.id}/agendamentos`);
    return { data: appointmentsRes.data };
  });
};

// Plans (public)
export const getPlans = () => request('GET', '/subscriptions/plans');

// Client subscription by phone (public)
export const getClientSubscription = (phone) => {
  const digits = phone.replace(/\D/g, '');
  return request('GET', `/subscriptions/cliente/${digits}`);
};

// Client lookup by phone (public)
export const findClientByPhone = (phone) => {
  const digits = phone.replace(/\D/g, '');
  return request('GET', `/clients/buscar?phone=${digits}`);
};

// Create client directly (public)
export const createClient = (name, phone, email, username, password) =>
  request('POST', '/clients/', {
    name, phone, username, password,
    ...(email ? { email } : {}),
  });

// Client login with phone+password (public)
export const loginByPhone = (phone, password) => {
  const digits = phone.replace(/\D/g, '');
  return request('POST', '/clients/login-phone', { phone: digits, password });
};

// Client login with username+password (public)
export const clientLogin = (username, password) =>
  request('POST', '/clients/login', { username, password });

// Set/update credentials for existing client identified by phone (public)
export const clientSetPassword = (phone, username, password) =>
  request('POST', '/clients/set-password', { phone, username, password });

// Create subscription for the client themselves, after payment confirmation (public)
// Preferir orderNsu quando disponível — o backend resolve client/plan/unit da tabela checkout_orders,
// eliminando dependência de localStorage que pode estar desatualizado.
export const createClientSubscription = (clientId, planId, unitId = null, orderNsu = null) => {
  const body = orderNsu
    ? { order_nsu: orderNsu }
    : { client_id: clientId, plan_id: planId, unit_id: unitId };
  return request('POST', '/subscriptions/cliente', body);
};

// Gera link de checkout InfinitePay para o cliente pagar o plano
export const createCheckoutLink = (clientId, planId, unitId = null) =>
  request('POST', '/subscriptions/checkout', { client_id: clientId, plan_id: planId, unit_id: unitId });

// ============== AUTH API ==============

/**
 * Login: o servidor define o cookie HttpOnly 'admin_token'.
 * O token também é retornado no body e salvo no localStorage como fallback
 * para navegadores mobile que bloqueiam cookies de terceiros.
 */
export const adminLogin = (username, password) =>
  request('POST', '/auth/login', { username, password });

/** Logout: limpa o cookie HttpOnly no servidor e o token do localStorage. */
export const adminLogout = () => request('POST', '/auth/logout');

/** Retorna dados do admin autenticado (role, barber_id, unit_id, etc.). */
export const getAdminInfo = () => request('GET', '/auth/me', null, true);

// ============== ADMIN API ==============

// Appointments
export const getAppointments = (params = {}) => {
  const qs = new URLSearchParams();

  // Single-day filter: date="2025-03-15" → date_from/date_to
  if (params.date) {
    qs.append('date_from', `${params.date}T00:00:00`);
    qs.append('date_to', `${params.date}T23:59:59`);
  }

  // Month range filter: date_from="2025-03-01" + date_to="2025-03-31"
  if (params.date_from && !params.date) {
    qs.append('date_from', `${params.date_from}T00:00:00`);
  }
  if (params.date_to && !params.date) {
    qs.append('date_to', `${params.date_to}T23:59:59`);
  }

  if (params.unit_id && params.unit_id !== 'all') qs.append('unit_id', params.unit_id);
  if (params.barber_id && params.barber_id !== 'all') qs.append('barber_id', params.barber_id);
  if (params.status && params.status !== 'all') qs.append('status', params.status);
  return request('GET', `/appointments/?${qs.toString()}`, null, true);
};

export const updateAppointment = (id, data) =>
  request('PUT', `/appointments/${id}`, data, true);

export const cancelAppointment = (id) =>
  request('PUT', `/appointments/${id}`, { status: 'cancelled' }, true);

export const deleteAppointment = (id) =>
  request('DELETE', `/appointments/${id}`, null, true);

// Barbers (admin)
// dataToSend pode incluir:
//   service_ids: string[]           — serviços sem preço customizado
//   service_prices: [{service_id, custom_price}]  — serviços com preço por barbeiro
export const createBarber = (data) => request('POST', '/barbers/', data, true);
export const updateBarber = (id, data) => request('PUT', `/barbers/${id}`, data, true);
export const deleteBarber = (id) => request('DELETE', `/barbers/${id}`, null, true);
export const deleteBarberPermanent = (id) => request('DELETE', `/barbers/${id}/permanent`, null, true);

/** Cria账号 de funcionário (barbeiro) no painel admin. */
export const createAdminEmployee = (data) => request('POST', '/auth/register', data, true);

// Uploads (ImgBB CDN — upload direto do frontend, sem passar pelo backend)
/**
 * Faz upload de uma imagem para o ImgBB e retorna a URL pública permanente.
 * Compatível com o contrato anterior: retorna { data: { url } }.
 */
export const uploadImage = async (file) => {
  const IMGBB_KEY = process.env.REACT_APP_IMGBB_API_KEY;
  if (!IMGBB_KEY) throw new Error('REACT_APP_IMGBB_API_KEY não configurada');

  // Converte o arquivo para base64 (ImgBB aceita base64 ou URL)
  const base64 = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // reader.result = "data:image/jpeg;base64,/9j/4AAQ..." — remove o prefixo
      const result = reader.result;
      resolve(result.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const body = new FormData();
  body.append('key', IMGBB_KEY);
  body.append('image', base64);

  const response = await fetch('https://api.imgbb.com/1/upload', {
    method: 'POST',
    body,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err?.error?.message || 'Falha no upload para ImgBB');
  }

  const json = await response.json();
  // ImgBB retorna: { data: { url, display_url, thumb: { url }, ... } }
  // Usamos display_url (URL direta da imagem sem paginação)
  return { data: { url: json.data.display_url || json.data.url } };
};


// Services (admin)
/** Lista todos os serviços incluindo inativos — uso exclusivo do painel admin. */
export const getServicesAdmin = () => request('GET', '/services/?include_inactive=true', null, true);
export const createService = (data) => request('POST', '/services/', data, true);
export const updateService = (id, data) => request('PUT', `/services/${id}`, data, true);
export const deleteService = (id) => request('DELETE', `/services/${id}`, null, true);
export const setServiceUnitPrice = (serviceId, unitId, price) =>
  request('PUT', `/services/${serviceId}/unit-prices/${unitId}`, { price }, true);
export const deleteServiceUnitPrice = (serviceId, unitId) =>
  request('DELETE', `/services/${serviceId}/unit-prices/${unitId}`, null, true);

// Promotions
export const getPromotions = (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (key !== 'active_only' && value !== undefined && value !== null && value !== '') qs.append(key, value);
  });
  const queryString = qs.toString();
  return request('GET', `/promotions/active${queryString ? '?' + queryString : ''}`);
};
export const getPromotionsAdmin = () => request('GET', '/promotions/', null, true);
export const createPromotion = (data) => request('POST', '/promotions/', data, true);
export const updatePromotion = (id, data) => request('PUT', `/promotions/${id}`, data, true);
export const pausePromotion = (id) => request('PATCH', `/promotions/${id}/pause`, null, true);
export const resumePromotion = (id) => request('PATCH', `/promotions/${id}/resume`, null, true);
export const deletePromotion = (id) => request('DELETE', `/promotions/${id}`, null, true);

// Units (admin)
export const createUnit = (data) => request('POST', '/units/', data, true);
export const updateUnit = (id, data) => request('PUT', `/units/${id}`, data, true);

// Dashboard
export const getDashboardStats = () => request('GET', '/dashboard/stats', null, true);

// Subscriptions (admin)
export const getSubscriptions = (statusFilter) => {
  const qs = statusFilter ? `?status=${statusFilter}` : '';
  // Sem barra antes de qs para evitar URL dupla: /subscriptions?status=x
  return request('GET', `/subscriptions${qs}`, null, true);
};
export const createSubscription = (data) => request('POST', '/subscriptions/', data, true);
export const getPlansAdmin = () => request('GET', '/subscriptions/plans?include_inactive=true', null, true);
export const createPlan = (data) => request('POST', '/subscriptions/plans', data, true);
export const updatePlan = (id, data) => request('PUT', `/subscriptions/plans/${id}`, data, true);
export const deletePlan = (id) => request('DELETE', `/subscriptions/plans/${id}`, null, true);
export const setPlanUnitPrice = (planId, unitId, price) =>
  request('PUT', `/subscriptions/plans/${planId}/unit-prices/${unitId}`, { price }, true);
export const deletePlanUnitPrice = (planId, unitId) =>
  request('DELETE', `/subscriptions/plans/${planId}/unit-prices/${unitId}`, null, true);
export const setPlanServices = (planId, services) =>
  request('PUT', `/subscriptions/plans/${planId}/services`, { services }, true);

// Admin subscription management
export const getAdminSubscriptions = (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') qs.append(key, value);
  });
  const queryString = qs.toString();
  return request('GET', `/subscriptions/admin${queryString ? '?' + queryString : ''}`, null, true);
};
export const cancelSubscription = (id) => request('PATCH', `/subscriptions/${id}/cancel`, null, true);
export const deleteSubscription = (id) => request('DELETE', `/subscriptions/${id}`, null, true);
export const getSubscriptionReport = (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') qs.append(key, value);
  });
  const queryString = qs.toString();
  return request('GET', `/subscriptions/report${queryString ? '?' + queryString : ''}`, null, true);
};

// Recurrences (admin)
export const getAdminRecurrences = (search = '') => {
  const qs = new URLSearchParams();
  if (search) qs.append('search', search);
  const queryString = qs.toString();
  return request('GET', `/appointments/recurrences${queryString ? '?' + queryString : ''}`, null, true);
};

export const getAdminRecurrenceDetail = (recurrenceId) =>
  request('GET', `/appointments/recurrences/${recurrenceId}`, null, true);

export const syncAdminRecurrenceRules = () =>
  request('POST', '/appointments/recurrences/sync-rules', null, true);

export const cancelAdminRecurrenceDate = (recurrenceId, appointmentDateIso) =>
  request('POST', `/appointments/recurrences/${recurrenceId}/cancel-date`, { appointment_date: appointmentDateIso }, true);

export const cancelAdminRecurrenceSeries = (recurrenceId) =>
  request('POST', `/appointments/recurrences/${recurrenceId}/cancel-all`, null, true);

export const renewAdminRecurrence = (recurrenceId) =>
  request('POST', `/appointments/recurrences/${recurrenceId}/renew`, null, true);

export const previewRecurrence = (payload) =>
  request('POST', '/appointments/recurrences/preview', payload, true);

export const createRecurrenceAdmin = (payload) =>
  request('POST', '/appointments/recurrences', payload, true);

export const editRecurrenceAdmin = (recurrenceId, payload) =>
  request('PATCH', `/appointments/recurrences/${recurrenceId}`, payload, true);

export const pauseRecurrenceAdmin = (recurrenceId, pauseEnd = null) =>
  request('POST', `/appointments/recurrences/${recurrenceId}/pause`, { pause_end: pauseEnd }, true);

export const resumeRecurrenceAdmin = (recurrenceId) =>
  request('POST', `/appointments/recurrences/${recurrenceId}/resume`, null, true);

export const listRecurrencesWithFilters = (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') params.append(k, v);
  });
  const qs = params.toString();
  return request('GET', `/appointments/recurrences${qs ? '?' + qs : ''}`, null, true);
};

// Clients (admin)
export const getClients = () => request('GET', '/clients/', null, true);

// Seed (dev only — requer login de admin)
export const seedDatabase = () => request('POST', '/seed', null, true).catch(() => { });

// Dias de fechamento
export const getClosedDays = (unitId) => request('GET', `/closed-days/?unit_id=${unitId}`);
export const createClosedDay = (data) => request('POST', '/closed-days/', data, true);
export const deleteClosedDay = (id) => request('DELETE', `/closed-days/${id}`, null, true);

// Super Admin
export const getQueueMetrics = () => request('GET', '/super-admin/queue/metrics', null, true);

export default {
  getUnits,
  getUnit,
  getBarbers,
  getBarberAdmin,
  getBarber,
  getServices,
  getService,
  getAvailableSlots,
  createAppointment,
  getAppointment,
  getAppointmentPublic,
  cancelAppointmentPublic,
  setAppointmentRecurrence,
  getAppointmentsByPhone,
  getPlans,
  getClientSubscription,
  findClientByPhone,
  createClient,
  clientLogin,
  clientSetPassword,
  createClientSubscription,
  createCheckoutLink,
  adminLogin,
  adminLogout,
  getAdminInfo,
  getAppointments,
  updateAppointment,
  cancelAppointment,
  deleteAppointment,
  createBarber,
  updateBarber,
  deleteBarber,
  deleteBarberPermanent,
  uploadImage,
  createService,
  updateService,
  deleteService,
  setServiceUnitPrice,
  deleteServiceUnitPrice,
  getPromotions,
  getPromotionsAdmin,
  createPromotion,
  updatePromotion,
  pausePromotion,
  resumePromotion,
  deletePromotion,
  createUnit,
  updateUnit,
  getDashboardStats,
  getSubscriptions,
  createSubscription,
  getPlansAdmin,
  createPlan,
  updatePlan,
  deletePlan,
  setPlanUnitPrice,
  deletePlanUnitPrice,
  setPlanServices,
  getAdminSubscriptions,
  cancelSubscription,
  deleteSubscription,
  getSubscriptionReport,
  getAdminRecurrences,
  getAdminRecurrenceDetail,
  syncAdminRecurrenceRules,
  cancelAdminRecurrenceDate,
  cancelAdminRecurrenceSeries,
  renewAdminRecurrence,
  previewRecurrence,
  createRecurrenceAdmin,
  editRecurrenceAdmin,
  pauseRecurrenceAdmin,
  resumeRecurrenceAdmin,
  listRecurrencesWithFilters,
  getClients,
  seedDatabase,
  getClosedDays,
  createClosedDay,
  deleteClosedDay,
  getQueueMetrics,
};
